import { History, Inbox, SlidersHorizontal, Users, type LucideIcon } from 'lucide-react';
import { Link, NavLink, Outlet } from 'react-router';
import logo from '../assets/brand/logo.svg?raw';
import { flatten, useInbox } from '../data/queries';
import { useT } from '../l10n/i18n';
import { useHeightVar } from '../ui/core/useHeightVar';
import { useAuth } from './auth';
import { Routes } from './routes';
import { useShellExtras } from './shellExtras';
import './shell.css';

/** A sidebar on wide screens, a bottom bar on phones, as the mobile app's rail and navigation bar. */
export function AppShell() {
  const t = useT();
  const { session } = useAuth();
  const extras = useShellExtras();
  const waiting = flatten(useInbox().data)?.length ?? 0;
  const navRef = useHeightVar('--nav-height');
  const destinations: { to: string; icon: LucideIcon; label: string; badge?: number }[] = [
    { to: Routes.inbox, icon: Inbox, label: t('navInbox'), badge: waiting },
    { to: Routes.team, icon: Users, label: t('navTeam') },
    { to: Routes.history, icon: History, label: t('navHistory') },
    { to: Routes.settings, icon: SlidersHorizontal, label: t('navSettings') },
  ];

  const links = destinations.map(({ to, icon: Icon, label, badge }) => (
    <NavLink key={to} to={to} className="nav-link">
      <span className="nav-icon">
        <Icon size={22} strokeWidth={2} aria-hidden />
        {badge ? <span className="nav-badge">{badge}</span> : null}
      </span>
      <span>{label}</span>
    </NavLink>
  ));

  return (
    <div className="shell">
      <aside className="sidebar">
        {/* Our own generated logo (brand/build.mjs). A router link, so it stays inside the app's base path. */}
        <Link to={Routes.inbox} className="sidebar-logo" aria-label="Talkos" dangerouslySetInnerHTML={{ __html: logo }} />
        <nav className="sidebar-nav" aria-label={t('appTitle')}>
          {links}
        </nav>
        {extras.sidebar}
        {session && (
          <div className="sidebar-who">
            <div className="title-small ellipsis">{session.name}</div>
            <div className="body-small ellipsis">{session.organizationName}</div>
          </div>
        )}
      </aside>
      <main className="shell-main">
        {extras.top}
        <Outlet />
      </main>
      <nav ref={navRef} className="bottom-nav" aria-label={t('appTitle')}>
        {links}
      </nav>
      {extras.overlay}
    </div>
  );
}
